import React from "react";
import { useNavigate } from "react-router";
import { Box, Typography, Button, Paper, TextField, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { FoodsIcon } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";

import { URLHandler } from "src/services/URLHandler";
import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";
import { IcvListContextProvider, useIcvListContext } from "./IcvListContext";
import { IcvGrid } from "./IcvGrid";
import { IcvModal } from "./IcvModal";

type IcvListProps = Record<string, never>;

const IcvListContent = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isFeatureActivated } = useApplicationParameters();

    const { searchIcv, setSearchIcv, showCreateModal, setShowCreateModal } = useIcvListContext();

    const handleCreateIcv = () => {
        setShowCreateModal(true);
    };

    const handleModalSuccess = () => {
        setShowCreateModal(false);
    };

    const handleCloseModal = () => {
        setShowCreateModal(false);
    };

    if (!isFeatureActivated("FS-icv-management")) {
        return <div>{t("common.accessDenied", "Access denied")}</div>;
    }

    return (
        <Box sx={{ padding: "32px" }}>
            <Paper
                sx={{
                    border: "1px solid #E7E6E4",
                    borderRadius: "16px",
                    overflow: "hidden",
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        padding: "32px",
                        borderBottom: "1px solid #E7E6E4",
                        backgroundColor: "white",
                    }}
                >
                    <Box
                        sx={{
                            width: "40px",
                            height: "40px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                        }}
                        onClick={() => navigate(URLHandler.settings.configuration())}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                "& svg": {
                                    strokeWidth: "2px",
                                    stroke: "currentColor",
                                },
                            }}
                        >
                            <FoodsIcon icon="arrowLeftShort" size={6} />
                        </Box>
                    </Box>
                    <Typography variant="number-inter" sx={{ lineHeight: "1.6em", color: "#393A36" }}>
                        {t("icvs.title", "Gestion des ICVs")}
                    </Typography>
                </Box>

                {/* Search and Add ICV Section */}
                <Box
                    sx={{
                        padding: "24px 32px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "24px",
                    }}
                >
                    {/* Search */}
                    <TextField
                        placeholder={t("icvs.search", "Search ICVs...")}
                        value={searchIcv}
                        onChange={(e) => setSearchIcv(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: "#A3A2A0", fontSize: "1.25rem" }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            maxWidth: "323px",
                            flex: 1,
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "24px",
                                minHeight: "40px",
                                backgroundColor: "white",
                                "& fieldset": {
                                    borderColor: "#E7E6E4",
                                },
                                "&:hover fieldset": {
                                    borderColor: "#D0D0D0",
                                },
                                "&.Mui-focused fieldset": {
                                    borderColor: "primary.main",
                                },
                            },
                            "& .MuiInputBase-input": {
                                fontSize: "0.875rem",
                                padding: "0px 16px !important",
                                color: "#393A36",
                                "&::placeholder": {
                                    color: "#A3A2A0",
                                    opacity: 1,
                                },
                            },
                        }}
                    />

                    {/* Action Buttons */}
                    <Box sx={{ display: "flex", gap: "12px", alignItems: "center" }}>
                        <Button
                            variant="contained"
                            startIcon={<FoodsIcon icon="plus" size={1} />}
                            onClick={handleCreateIcv}
                            sx={(theme) => ({
                                borderRadius: "44px",
                                padding: "0px 20px",
                                height: "40px",
                                fontWeight: 600,
                                fontSize: "0.875rem",
                                textTransform: "none",
                                background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                color: "white !important",
                                boxShadow: "0px 1px 2px 0px rgba(42, 43, 39, 0.02)",
                                "&:hover": {
                                    background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                                    boxShadow: "0px 1px 3px 0px rgba(42, 43, 39, 0.03)",
                                    color: "white !important",
                                },
                                "&:disabled": {
                                    background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                    opacity: 0.36,
                                    color: "white !important",
                                },
                            })}
                        >
                            {t("icvs.addIcv", "Add ICV")}
                        </Button>
                    </Box>
                </Box>

                {/* ICVs Grid */}
                <Box sx={{ padding: "0px 32px 32px" }}>
                    <IcvGrid />
                </Box>
            </Paper>

            {/* Create ICV Modal */}
            <IcvModal
                open={showCreateModal}
                onClose={handleCloseModal}
                onSuccess={handleModalSuccess}
                editingIcv={undefined}
            />
        </Box>
    );
};

export const IcvList = (props: IcvListProps) => {
    return (
        <IcvListContextProvider>
            <IcvListContent />
        </IcvListContextProvider>
    );
};
